import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import AskAddyV2 from "../../models/askAddyV2";
import { AppRow } from "./AppRow";
import { TOAST_OPTIONS } from "./toastOptions";

const SIGN_IN_OPTIONS = [
  { value: "none", label: "None" },
  { value: "apiKey", label: "API key" },
  { value: "bearer", label: "Bearer token" },
  { value: "oauth", label: "OAuth" },
];

const EMPTY_SERVER = {
  name: "",
  serverUrl: "",
  authType: "none",
  apiKeyHeaderName: "",
  apiKeyValue: "",
  bearerToken: "",
  oauthClientId: "",
  oauthClientSecret: "",
  oauthScope: "",
};

/** What a server's row says under its name. */
const describeServer = ({ status, lastError, toolCount }) => {
  if (status === "connected") {
    return `Connected · ${toolCount} tool${toolCount === 1 ? "" : "s"}`;
  }
  if (status === "needs_auth") {
    return "Sign in to connect";
  }
  if (status === "error") {
    return lastError ? `Couldn't connect: ${lastError}` : "Couldn't connect";
  }

  return "Checking…";
};

/**
 * Opens a sign-in page in a new tab. The tab is opened before the request so the browser doesn't
 * block it as a pop-up; the sign-in page gets no handle on the sidebar.
 */
export const openSignIn = async (getUrl) => {
  const tab = window.open("", "_blank");
  if (tab) {
    tab.opener = null;
  }
  try {
    const { url } = await getUrl();
    if (tab) {
      tab.location.href = url;
    } else {
      window.open(url, "_blank", "noopener");
    }
  } catch (error) {
    tab?.close();
    throw error;
  }
};

/** Adding a server: its name and URL, and how it signs in. */
const AddMcpServerForm = ({ isBusy, onAdd, onCancel }) => {
  const [server, setServer] = useState(EMPTY_SERVER);
  const set = (field) => (event) => setServer((current) => ({ ...current, [field]: event.target.value }));
  const hasSecret =
    (server.authType !== "apiKey" || server.apiKeyValue.trim()) && (server.authType !== "bearer" || server.bearerToken.trim());
  const isReady = server.name.trim() && server.serverUrl.trim() && hasSecret;

  return (
    <form
      className="lendy-profile-form"
      aria-label="Add MCP server"
      onSubmit={(event) => {
        event.preventDefault();
        onAdd(server);
      }}
    >
      <label>
        Name
        <input required autoFocus value={server.name} placeholder="My CRM" maxLength={60} onChange={set("name")} />
      </label>
      <label>
        Server URL
        <input required type="url" value={server.serverUrl} placeholder="https://example.com/mcp" onChange={set("serverUrl")} />
      </label>
      <div className="lendy-profile-form-field" role="group" aria-label="Sign-in">
        <span className="lendy-profile-form-label">Sign-in</span>
        <div className="lendy-profile-segments">
          {SIGN_IN_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              aria-pressed={server.authType === value}
              onClick={() => setServer((current) => ({ ...current, authType: value }))}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {server.authType === "apiKey" && (
        <>
          <label>
            Header name
            <input value={server.apiKeyHeaderName} placeholder="x-api-key" onChange={set("apiKeyHeaderName")} />
          </label>
          <label>
            API key
            <input required type="password" autoComplete="off" value={server.apiKeyValue} onChange={set("apiKeyValue")} />
          </label>
        </>
      )}
      {server.authType === "bearer" && (
        <label>
          Bearer token
          <input required type="password" autoComplete="off" value={server.bearerToken} onChange={set("bearerToken")} />
        </label>
      )}
      {server.authType === "oauth" && (
        <>
          <label>
            Client ID (optional)
            <input value={server.oauthClientId} placeholder="Only if the server can't register one itself" onChange={set("oauthClientId")} />
          </label>
          <label>
            Client secret (optional)
            <input type="password" autoComplete="off" value={server.oauthClientSecret} onChange={set("oauthClientSecret")} />
          </label>
          <label>
            Scope (optional)
            <input value={server.oauthScope} onChange={set("oauthScope")} />
          </label>
        </>
      )}
      <div className="lendy-profile-replies">
        <button type="submit" className="lendy-pill-button lendy-pill-button--go" disabled={isBusy || !isReady}>
          {isBusy ? "Adding…" : server.authType === "oauth" ? "Add and sign in" : "Add server"}
        </button>
        <button type="button" className="lendy-btn lendy-btn--text" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
};

/**
 * The loan officer's own MCP servers, whose tools the chat can use: loaded here, listed with the
 * apps on the Apps & MCP page. An OAuth server signs in in a new tab; the list
 * refreshes when they come back.
 */
export const useMcpServers = (identity) => {
  const [servers, setServers] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [confirmingId, setConfirmingId] = useState("");
  const [error, setError] = useState("");
  const [hasLoadFailed, setHasLoadFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const page = await AskAddyV2.mcpServers.list(identity);
      // An agent without these routes yet answers with no data: the section waits for it quietly.
      setIsEnabled(page?.enabled === true);
      setServers(page?.servers || []);
      setHasLoadFailed(false);
    } catch {
      // The Apps tab already shows why the agent refused (identity, sign-in), so this stays short.
      setHasLoadFailed(true);
      setServers((current) => current || []);
    }
  }, [identity]);

  // Back from a server's sign-in tab: pick up the connection.
  useEffect(() => {
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [load]);

  const run = async (id, action) => {
    setBusyId(id);
    setError("");
    try {
      await action();
      await load();
    } catch (actionError) {
      setError(actionError.message);
    } finally {
      setBusyId("");
    }
  };

  const add = (server) =>
    run("new", async () => {
      const added = await AskAddyV2.mcpServers.add(server, identity);
      setIsAdding(false);
      if (server.authType === "oauth" && added?.id) {
        await openSignIn(() => AskAddyV2.mcpServers.connect(added.id, identity));
      }
    });

  const remove = (id) => run(id, () => AskAddyV2.mcpServers.remove(id, identity)).then(() => setConfirmingId(""));
  const signIn = (id) => run(id, () => openSignIn(() => AskAddyV2.mcpServers.connect(id, identity)));
  // A retry that ends the way it started looks like nothing happened: the toast says how it went.
  const retry = (id) =>
    run(id, async () => {
      const server = (await AskAddyV2.mcpServers.scan(id, identity))?.server;
      if (server?.status === "connected") {
        toast.success(`${server.name} is connected`, TOAST_OPTIONS);

        return;
      }

      toast.error(`Still can't connect to ${server?.name || "this server"}.`, TOAST_OPTIONS);
    });

  return { servers, isEnabled, isAdding, setIsAdding, busyId, confirmingId, setConfirmingId, error, hasLoadFailed, add, remove, signIn, retry };
};

/** What went wrong with the servers, above their rows. */
export const McpNotice = ({ mcp }) => (
  <>
    {mcp.error && <p className="aav2-apps-note aav2-apps-note--error">{mcp.error}</p>}
    {mcp.hasLoadFailed && <p className="aav2-apps-note aav2-apps-note--error">Couldn't load your servers.</p>}
  </>
);

/** "Custom MCP": adds the loan officer's own server, in a form that opens under its row. */
export const AddMcpServerRow = ({ mcp }) => {
  const { isAdding, setIsAdding, busyId, add } = mcp;

  return (
    <AppRow
      app={{ name: "Custom MCP" }}
      icon={<span className="aav2-apps-logo">+</span>}
      detail="Add your own server"
      actions={
        !isAdding && (
          <button type="button" className="lendy-btn lendy-btn--text aav2-apps-link" aria-label="Add Custom MCP" onClick={() => setIsAdding(true)}>
            Add
          </button>
        )
      }
      below={isAdding && <AddMcpServerForm isBusy={busyId === "new"} onAdd={add} onCancel={() => setIsAdding(false)} />}
    />
  );
};

/** The servers they added that match `search`, each with what it needs (sign in, try again) and Remove. */
export const McpServerRows = ({ mcp, search }) => {
  const { servers, busyId, confirmingId, setConfirmingId } = mcp;
  const query = search.trim().toLowerCase();

  return (servers || [])
    .filter(({ name }) => name.toLowerCase().includes(query))
    .map((server) => {
      const isConfirming = confirmingId === server.id;
      const isBusy = busyId === server.id;

      return (
        <AppRow
          key={server.id}
          app={{ name: server.name }}
          detail={describeServer(server)}
          actions={
            isConfirming ? (
              <>
                <button type="button" className="lendy-pill-button lendy-pill-button--danger" disabled={isBusy} onClick={() => mcp.remove(server.id)}>
                  Remove
                </button>
                <button type="button" className="lendy-btn lendy-btn--text" onClick={() => setConfirmingId("")}>
                  Keep
                </button>
              </>
            ) : (
              <>
                {server.status === "needs_auth" && (
                  <button type="button" className="lendy-btn lendy-btn--text aav2-apps-link" disabled={isBusy} onClick={() => mcp.signIn(server.id)}>
                    Sign in
                  </button>
                )}
                {server.status === "error" && (
                  <button type="button" className="lendy-btn lendy-btn--text aav2-apps-link" disabled={isBusy} onClick={() => mcp.retry(server.id)}>
                    {isBusy ? "Trying…" : "Try again"}
                  </button>
                )}
                <button type="button" className="lendy-btn lendy-btn--text" aria-label={`Remove ${server.name}`} onClick={() => setConfirmingId(server.id)}>
                  Remove
                </button>
              </>
            )
          }
        />
      );
    });
};
